import { SECURITY_NAME_ALIAS } from 'common/constants/cap-table/capTable';
import type { ReviewBatchItem, ReviewBatchItemResult } from 'services/captable';
import type { MappingReferenceData } from 'services/reference-generator/types';
import type { ReviewItem } from 'store/useReviewStore';
import { mappingProposalIdsFor } from './mappingReferenceStatus';
import type { MappingReviewAction, MappingReviewChange } from './settleMappingReview';

/**
 * Accept-time client gate: a card whose suggestion the grid cannot apply (an option name that
 * matches nothing) never reaches the server and is reported like a server failure.
 */
export const partitionAcceptableItems = (
  items: ReviewItem[],
  errorFor: (item: ReviewItem) => string | undefined
): { acceptable: ReviewItem[]; failures: BulkFailure[] } => {
  const failures: BulkFailure[] = [];
  const acceptable = items.filter(card => {
    const reason = errorFor(card);
    if (reason) {
      failures.push({ card, reason });
    }
    return !reason;
  });
  return { acceptable, failures };
};

/** The decision a bulk action applies to every selected card. */
export type ReviewBulkAction = 'accept' | 'decline' | 'reopen';

const EVIDENCE_ACTION: Record<ReviewBulkAction, MappingReviewAction> = {
  accept: 'accept',
  decline: 'dismiss',
  reopen: 'reopen',
};

/** The citation and Excel review action a bulk action applies: a decline dismisses. */
export const evidenceActionOf = (action: ReviewBulkAction): MappingReviewAction => EVIDENCE_ACTION[action];

/** A selected card and the batch item that decides it. */
export type ReviewBatchEntry = { card: ReviewItem; item: ReviewBatchItem };

/** A card a bulk action left as it was, and why. */
export type BulkFailure = { card: ReviewItem; reason: string };

/**
 * One batch item per card, naming only what that card decides: its field, its AI citation, and the
 * Excel proposal the same single-card decision would settle. A card with nothing left to decide is
 * skipped, not failed: its cell already holds a decision, which the card rebuild shows.
 */
export const buildReviewBatch = (
  cards: ReviewItem[],
  action: ReviewBulkAction,
  mappingRowsOf: (card: ReviewItem) => MappingReferenceData[] | undefined
): ReviewBatchEntry[] =>
  cards.flatMap(card => {
    const citationId = !card.canSettle && card.canSettleCitation ? card.primaryCitation?.id : undefined;
    const item: ReviewBatchItem = {
      security_id: card.securityId,
      field_names: card.canSettle ? [card.fieldAlias] : [],
      citation_ids: citationId != null ? [citationId] : [],
      // A security-name finding decides its column, never an Excel reference.
      mapping_ids:
        card.canSettle && card.fieldAlias === SECURITY_NAME_ALIAS
          ? []
          : mappingProposalIdsFor(mappingRowsOf(card), evidenceActionOf(action), card.status),
    };
    return item.field_names.length + item.citation_ids.length + item.mapping_ids.length > 0 ? [{ card, item }] : [];
  });

/** A card the server settled, with its result. */
export type SettledPair = { entry: ReviewBatchEntry; result: ReviewBatchItemResult };

/** The cards the server settled, each with its result, and the ones it left as they were, with why. */
export type BatchPairs = {
  settled: SettledPair[];
  failed: { entry: ReviewBatchEntry; result: ReviewBatchItemResult | undefined; message: string }[];
};

/**
 * Pair each card with the result at its request position, whatever order the server ran the items in.
 * A failed, missing or mismatched result leaves its card as it was; results past the request are ignored.
 */
export const pairBatchResults = (entries: ReviewBatchEntry[], results: ReviewBatchItemResult[]): BatchPairs => {
  const pairs: BatchPairs = { settled: [], failed: [] };
  entries.forEach((entry, index) => {
    const result = results[index];
    if (result?.status === 'settled' && result.errors.length === 0 && result.security_id === entry.item.security_id) {
      pairs.settled.push({ entry, result });
    } else {
      pairs.failed.push({ entry, result, message: result?.errors.join('; ') || 'Not settled by the server' });
    }
  });
  return pairs;
};

const PAST_TENSE: Record<ReviewBulkAction, string> = { accept: 'accepted', decline: 'declined', reopen: 'reopened' };

/** One toast line naming every card a bulk action could not decide. */
export const bulkFailureSummary = (action: ReviewBulkAction, failures: BulkFailure[]): string =>
  `${failures.length} item(s) could not be ${PAST_TENSE[action]}: ${failures
    .map(({ card }) => `${card.securityLabel} - ${card.fieldLabel}`)
    .join(', ')}.`;

/** A settled item's Excel row changes, in the shape the in-place mapping mirrors apply. */
export const mappingChangeOf = (
  { mappings, retired_ids: retiredIds, restored }: SettledPair['result'],
  action: ReviewBulkAction
): MappingReviewChange => ({
  mappingIds: mappings.map(row => row.id),
  action: evidenceActionOf(action),
  retiredIds,
  restored,
  updatedAtById: new Map(mappings.flatMap(row => (row.updated_at ? [[row.id, row.updated_at] as const] : []))),
});

/** Every settled field per security: the securities patch carries them all, so a rebuild resurrects none. */
export const settledFieldsBySecurity = (settled: SettledPair[]): Map<number, string[]> => {
  const bySecurity = new Map<number, string[]>();
  settled.forEach(({ result: { security_id: securityId, settled_fields: fields } }) => {
    if (fields.length > 0) {
      bySecurity.set(securityId, [...(bySecurity.get(securityId) ?? []), ...fields]);
    }
  });
  return bySecurity;
};
